// =============================================================================
// TrackerModal.jsx
//
// Modal de criar / editar uma entrada do Tracker.
//
// Contém 4 componentes internos + o modal principal (exportado):
//   SectionLabel → separador visual de secção
//   PhaseCard    → card editável de uma fase (Phase 1, Rollback…)
//   ServerCard   → cascade OMC_ID → TAC → Sites
//   TrackerModal → modal principal
//
// O SiteListDialog vive no TrackerPage (não aqui) para evitar o bug do MUI
// em que o fecho de um Dialog dispara o backdrop do outro.
//
// Mudanças para MUI v6 (@mui/material ^6.4.1):
//   → Grid: usa Grid2 (import de @mui/material/Grid2) com prop size={}
//     em vez de <Grid item xs={} sm={}>
//   → TextField: InputProps → slotProps.input
//                InputLabelProps → slotProps.inputLabel
// =============================================================================

import React, { useState, useEffect } from 'react';
import Grid from '@mui/material/Grid2';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Button, TextField, MenuItem, Typography, Alert,
  IconButton, Box, Divider, CircularProgress,
  Chip, Tooltip, LinearProgress, InputAdornment, Checkbox, Switch,
  Autocomplete,
} from '@mui/material';
import AddIcon           from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import CloseIcon         from '@mui/icons-material/Close';
import ListAltIcon       from '@mui/icons-material/ListAlt';
import {
  apiGetServers,
  apiGetTechnologies,
  apiGetVendors,
  apiGetFeatures,
  apiGetTeams,
  apiGetOwnersByTeam,
  apiGetServersBySites,
} from '../requests/trackerRequests';
import { DatePicker }            from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider }  from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterMoment }         from '@mui/x-date-pickers/AdapterMoment';
import moment                    from 'moment';


// =============================================================================
// UTILITÁRIOS
// =============================================================================

// ID temporário para phases/servers criados no browser.
// Prefixo "tmp_" torna claro que ainda não existe na BD.
const uid = () => `tmp_${Date.now()}_${Math.random().toString(36).slice(2)}`;

const makePhase  = (label = 'Phase 1') => ({ id: uid(), label, date: '', crq: '', comments: '' });
const makeServer = () => ({ id: uid(), omcId: '', tacs: [], allSites: [], selectedSites: [], siteToTac: {} });

// Normaliza um server vindo da API para o formato esperado pelo ServerCard.
// A API devolve: { id, omcId, tacs: [{ id, tac, sites[] }], selectedSites[] }
// O frontend precisa adicionalmente de: allSites[] e siteToTac map
const normalizeServer = (s) => {
  const tacs = Array.isArray(s.tacs) ? s.tacs : [];
  // allSites = união de todos os sites de todos os TACs
  const allSites = tacs.flatMap(t => t.sites ?? []).filter((v, i, a) => a.indexOf(v) === i).sort();
  // siteToTac map: site → tac string
  const siteToTac = {};
  tacs.forEach(t => (t.sites ?? []).forEach(site => { if (!siteToTac[site]) siteToTac[site] = t.tac; }));
  return {
    ...makeServer(),
    ...s,
    tacs,
    allSites,
    selectedSites: Array.isArray(s.selectedSites) ? s.selectedSites : allSites,
    siteToTac,
  };
};

// Normaliza datas da API para "yyyy-MM-dd" que o input[type="date"] exige.
//
// Formatos confirmados da API:
//   "02/22/2026 00:00:00" → MM/dd/yyyy (SQL Server cultura en-US com barras)
//   "2025-02-24"          → yyyy-MM-dd (ISO correcto — já está bem)
//   "2026-22-02T00:00:00" → yyyy-dd-MM (dia e mês trocados — detectado pela regra >12)
//
// Regra de desambiguação para hífens (yyyy-A-B):
//   Se A > 12 → A é o dia, B é o mês → trocar
//   Caso contrário → já está em yyyy-MM-dd
const toDateInput = (raw) => {
  if (!raw) return '';
  const s = String(raw);

  // Formato com barras: MM/dd/yyyy HH:mm:ss  (formato confirmado da API)
  const slashMatch = s.match(/^(\d{2})\/(\d{2})\/(\d{4})/);
  if (slashMatch) {
    const [, mm, dd, yyyy] = slashMatch;
    return `${yyyy}-${mm}-${dd}`;
  }

  // Formato com hífens: yyyy-A-B (ISO ou yyyy-dd-MM)
  const dashMatch = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (dashMatch) {
    const [, yyyy, a, b] = dashMatch;
    // Se A > 12 → está em yyyy-dd-MM → trocar
    return Number(a) > 12
      ? `${yyyy}-${b}-${a}`
      : `${yyyy}-${a}-${b}`;
  }

  // Fallback genérico via Date
  const d = new Date(s);
  return isNaN(d.getTime()) ? '' : d.toISOString().slice(0, 10);
};

const emptyForm = () => ({
  title: '', technology: '', vendor: '', feature: '',
  startDate: '', endDate: '',
  teamId: '', teamName: '', ownerIds: [], ownerNames: [],
  servers:  [],
  phases:   [],
  comments: '',
  status: 'Open',
});


// =============================================================================
// SectionLabel — separador visual de secção
// =============================================================================
const SectionLabel = ({ children }) => (
  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2, mt: 0.5 }}>
    <Typography variant="overline" sx={{
      color: 'text.disabled', fontSize: '0.63rem',
      fontWeight: 700, letterSpacing: 1.5, lineHeight: 1, whiteSpace: 'nowrap',
    }}>
      {children}
    </Typography>
    <Divider sx={{ flex: 1 }} />
  </Box>
);


// =============================================================================
// PhaseCard — card editável de uma fase
//
// Props:
//   phase    → { id, label, date, crq, comments }
//   onChange → callback(phaseActualizada)
//   onRemove → callback()
// =============================================================================
const PhaseCard = ({ phase, onChange, onRemove }) => {
  const isRollback = phase.label?.startsWith('Rollback');

  return (
    <Box sx={{
      border: '1px solid',
      borderColor: isRollback ? 'error.dark' : 'divider',
      borderRadius: 2, p: 2,
      bgcolor: isRollback ? 'rgba(244,67,54,0.04)' : 'action.hover',
    }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
        <TextField
          size="small"
          value={phase.label}
          onChange={(e) => onChange({ ...phase, label: e.target.value })}
          placeholder="Phase name"
          sx={{
            width: 150,
            '& input': { fontWeight: 700, fontSize: '0.8rem', color: isRollback ? 'error.main' : 'inherit' },
          }}
        />
        <IconButton size="small" onClick={onRemove} color="error">
          <CloseIcon fontSize="small" />
        </IconButton>
      </Box>

      {/* MUI v6: Grid2 com size= em vez de item xs= sm= */}
      <LocalizationProvider dateAdapter={AdapterMoment}>
      <Grid container spacing={1.5}>
        <Grid size={{ xs: 12, sm: 3 }}>
          <DatePicker
            label="Date"
            format="YYYY-MM-DD"
            value={phase.date ? moment(phase.date, 'YYYY-MM-DD') : null}
            onChange={(val) => onChange({ ...phase, date: val?.isValid() ? val.format('YYYY-MM-DD') : '' })}
            slotProps={{ textField: { fullWidth: true, size: 'small' } }}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 3 }}>
          <TextField
            fullWidth size="small" label="CRQ"
            value={phase.crq}
            onChange={(e) => onChange({ ...phase, crq: e.target.value })}
            placeholder="CRQ-YYYY-XXXX"
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6 }}>
          <TextField
            fullWidth size="small" label="Comments"
            value={phase.comments}
            onChange={(e) => onChange({ ...phase, comments: e.target.value })}
            placeholder="Notes for this phase…"
          />
        </Grid>
      </Grid>
      </LocalizationProvider>
    </Box>
  );
};


// =============================================================================
// ServerCard — cascade OMC_ID → TAC → botão Sites
//
// Props:
//   server         → { id, omcId, tac, allSites, selectedSites }
//   technology     → string — tecnologia do formulário pai
//   onChange       → callback(serverActualizado)
//   onRemove       → callback()
//   onOpenSiteList → callback(server) — avisa o TrackerPage para abrir o dialog
// =============================================================================
const ServerCard = ({ server, technology, vendor, onChange, onRemove, onOpenSiteList }) => {
  const [serverRows, setServerRows] = useState([]);
  const [omcOptions, setOmcOptions] = useState([]);
  const [tacOptions, setTacOptions] = useState([]);
  const [loading,    setLoading]    = useState(Boolean(technology));
  const [loadError,  setLoadError]  = useState(null);

  // tacs is now [{ id, tac, sites[] }] — extract string list for Autocomplete
  const selectedTacs = (server.tacs ?? []).map(t => t.tac);

  useEffect(() => {
    if (!technology || !vendor) return;
    setLoading(true);
    setLoadError(null);

    apiGetServers(technology, vendor)
      .then((res) => {
        const rows = res.data ?? [];
        setServerRows(rows);

        const omcFromApi   = [...new Set(rows.map((r) => r.omcId).filter(Boolean))];
        const omcWithSaved = server.omcId && !omcFromApi.includes(server.omcId)
          ? [...omcFromApi, server.omcId]
          : omcFromApi;
        setOmcOptions(omcWithSaved.sort());

        if (server.omcId) {
          const tacFromApi = [...new Set(rows.filter((r) => r.omcId === server.omcId).map((r) => r.tac))].sort();
          setTacOptions(tacFromApi);
        }

        // Reconstruct allSites + siteToTac for all selected TACs in edit mode
        if (server.omcId && selectedTacs.length > 0) {
          const allSites = rows
            .filter((r) => r.omcId === server.omcId && selectedTacs.includes(r.tac))
            .map((r) => r.csr)
            .filter((v, i, a) => a.indexOf(v) === i)
            .sort();
          const siteToTac = {};
          rows.filter((r) => r.omcId === server.omcId && selectedTacs.includes(r.tac))
              .forEach((r) => { if (!siteToTac[r.csr]) siteToTac[r.csr] = r.tac; });
          if (allSites.length > 0)
            onChange({ ...server, allSites, siteToTac });
        }
      })
      .catch(() => setLoadError('Failed to load servers.'))
      .finally(() => setLoading(false));
  }, [technology, vendor]);

  const handleOmcChange = (omcId) => {
    setTacOptions(
      [...new Set(serverRows.filter((r) => r.omcId === omcId).map((r) => r.tac))].sort()
    );
    onChange({ ...server, omcId, tacs: [], allSites: [], selectedSites: [], siteToTac: {} });
  };

  // Multi-TAC change → rebuild tacs structure with sites per TAC
  const handleTacsChange = (newTacStrings) => {
    // Build new tacs array preserving existing sites if TAC was already selected
    const existingTacMap = Object.fromEntries((server.tacs ?? []).map(t => [t.tac, t]));
    const newTacs = newTacStrings.map((tac, idx) => ({
      id:    existingTacMap[tac]?.id ?? `tmp_${uid()}`,
      tac,
      sites: existingTacMap[tac]?.sites ?? [],
      sortOrder: idx,
    }));

    // Rebuild allSites from All_Cellsites_FINAL for any TAC that has no sites yet
    const allSites = serverRows
      .filter((r) => r.omcId === server.omcId && newTacStrings.includes(r.tac))
      .map((r) => r.csr)
      .filter((v, i, a) => a.indexOf(v) === i)
      .sort();

    // Default sites for each TAC = all sites from DB for that TAC
    const filledTacs = newTacs.map(t => ({
      ...t,
      sites: t.sites.length > 0
        ? t.sites
        : serverRows.filter(r => r.omcId === server.omcId && r.tac === t.tac).map(r => r.csr).sort()
    }));

    const siteToTac = {};
    serverRows.filter((r) => r.omcId === server.omcId && newTacStrings.includes(r.tac))
              .forEach((r) => { if (!siteToTac[r.csr]) siteToTac[r.csr] = r.tac; });

    onChange({
      ...server,
      tacs:          filledTacs,
      allSites,
      selectedSites: allSites,
      siteToTac,
    });
  };

  const allSites      = server.allSites      ?? [];
  const selectedSites = server.selectedSites ?? [];
  const hasSites      = allSites.length > 0;
  const hasExclusions = selectedSites.length < allSites.length;

  return (
    <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, p: 2, bgcolor: 'action.hover' }}>
      {loading && <LinearProgress sx={{ mb: 1, borderRadius: 1 }} />}

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>

        {/* OMC_ID */}
        <Autocomplete
          options={loading ? [] : omcOptions}
          value={loading ? null : (server.omcId || null)}
          onChange={(_, val) => val && handleOmcChange(val)}
          disabled={loading}
          sx={{ minWidth: 220 }}
          renderInput={(params) => (
            <TextField
              {...params}
              size="small"
              label="Server (OMC_ID)"
              placeholder="Search…"
              slotProps={{
                input: {
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {loading && <CircularProgress size={14} />}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                },
              }}
            />
          )}
        />


        {/* TAC — multi-select with checkboxes + Select All */}
        <Autocomplete
          multiple
          disableCloseOnSelect
          options={tacOptions.length > 0 ? ['__SELECT_ALL__', ...tacOptions] : []}
          value={selectedTacs}
          onChange={(_, val) => {
            if (val.includes('__SELECT_ALL__')) {
              // toggle: if all selected → deselect all, else select all
              const allSelected = tacOptions.every(t => selectedTacs.includes(t));
              handleTacsChange(allSelected ? [] : tacOptions);
            } else {
              handleTacsChange(val);
            }
          }}
          disabled={!server.omcId || loading || tacOptions.length === 0}
          sx={{ minWidth: 220 }}
          getOptionLabel={(option) => option === '__SELECT_ALL__' ? 'Select All' : option}
          renderOption={(props, option, { selected }) => {
            if (option === '__SELECT_ALL__') {
              const allSelected = tacOptions.length > 0 && tacOptions.every(t => selectedTacs.includes(t));
              return (
                <li {...props} key="__SELECT_ALL__">
                  <Checkbox checked={allSelected} indeterminate={selectedTacs.length > 0 && !allSelected}
                    size="small" sx={{ mr: 1, p: 0 }} />
                  <Typography sx={{ fontSize: '0.8rem', fontWeight: 600 }}>Select All</Typography>
                </li>
              );
            }
            return (
              <li {...props}>
                <Checkbox checked={selected} size="small" sx={{ mr: 1, p: 0 }} />
                {option}
              </li>
            );
          }}
          renderTags={(value, getTagProps) =>
            value.map((opt, idx) => (
              <Chip
                {...getTagProps({ index: idx })}
                key={opt}
                label={opt}
                size="small"
                sx={{ height: 20, fontSize: '0.7rem' }}
              />
            ))
          }
          renderInput={(params) => (
            <TextField
              {...params}
              size="small"
              label={selectedTacs.length > 1 ? `TAC/LAC (${selectedTacs.length})` : 'TAC/LAC'}
              placeholder={selectedTacs.length === 0 ? 'Search…' : ''}
            />
          )}
        />

        {/* Site List — visible once at least one TAC is selected */}
        {hasSites && (
          <Tooltip title={
            hasExclusions
              ? `${selectedSites.length} of ${allSites.length} sites selected`
              : 'All sites selected'
          }>
            <Button
              size="small" variant="outlined"
              startIcon={<ListAltIcon />}
              color={hasExclusions ? 'warning' : 'primary'}
              onClick={() => onOpenSiteList(server)}
            >
              Site List
              <Chip
                label={`${selectedSites.length}/${allSites.length}`}
                size="small"
                sx={{
                  ml: 0.75, height: 18, fontSize: '0.65rem',
                  bgcolor: hasExclusions ? 'warning.light' : 'primary.light',
                  color:   hasExclusions ? 'warning.dark'  : 'primary.dark',
                }}
              />
            </Button>
          </Tooltip>
        )}

        <Box sx={{ flex: 1 }} />
        <IconButton size="small" onClick={onRemove} color="error">
          <CloseIcon fontSize="small" />
        </IconButton>
      </Box>

      {loadError && (
        <Typography variant="caption" color="error" sx={{ mt: 1, display: 'block' }}>
          {loadError}
        </Typography>
      )}
    </Box>
  );
};



// =============================================================================
// SiteLookupCard — "Add by Site" card
//
// Fluxo:
//   1. Utilizador escreve sites separados por vírgula no campo de texto
//   2. Clica "Lookup" → chama apiGetServersBySites
//   3. Resultados agrupados por OMC_ID + TAC aparecem com checkboxes
//   4. Utilizador pode desmarcar grupos antes de confirmar
//   5. "Confirm" converte os grupos seleccionados em ServerDtos normais
//      e chama onConfirm([...serverDtos])
//   6. "Cancel" remove este card sem adicionar nada
//
// Props:
//   technology → string (herdada do form pai)
//   onConfirm  → callback([{ omcId, tac, allSites, selectedSites }])
//   onCancel   → callback()
// =============================================================================
const SiteLookupCard = ({ technology, onConfirm, onCancel }) => {
  const [siteInput, setSiteInput] = useState('');
  const [loading,   setLoading]   = useState(false);
  const [error,     setError]     = useState(null);
  const [groups,    setGroups]    = useState(null);
  const [notFound,  setNotFound]  = useState([]);

  const handleLookup = () => {
    const sites = siteInput.split(',').map((s) => s.trim()).filter(Boolean);
    if (!sites.length) return;
    if (!technology) { setError('Select a Technology first.'); return; }

    setLoading(true);
    setError(null);
    setGroups(null);
    setNotFound([]);

    apiGetServersBySites(technology, sites)
      .then((res) => {
        const rows    = res.data ?? [];
        const found   = rows.filter((r) => r.found);
        const missing = rows.filter((r) => !r.found).map((r) => r.csr);
        setNotFound(missing);

        if (!found.length) { setGroups([]); return; }

        const map = new Map();
        for (const r of found) {
          const key = `${r.omcId}||${r.tac}`;
          if (!map.has(key)) map.set(key, { omcId: r.omcId, tac: r.tac, sites: [], checked: true });
          map.get(key).sites.push(r.csr);
        }
        setGroups([...map.values()]);
      })
      .catch(() => setError('Lookup failed. Please try again.'))
      .finally(() => setLoading(false));
  };

  const toggleGroup = (idx) =>
    setGroups((prev) => prev.map((g, i) => i === idx ? { ...g, checked: !g.checked } : g));

  const handleConfirm = () => {
    const servers = (groups ?? [])
      .filter((g) => g.checked)
      .map((g) => {
        const tacObj = {
          id:    `tmp_tac_${Date.now()}_${Math.random().toString(36).slice(2)}`,
          tac:   g.tac,
          sites: [...g.sites].sort(),
        };
        return {
          id:            `tmp_${Date.now()}_${Math.random().toString(36).slice(2)}`,
          omcId:         g.omcId,
          tacs:          [tacObj],
          allSites:      [...g.sites].sort(),
          selectedSites: [...g.sites].sort(),
          siteToTac:     Object.fromEntries(g.sites.map(s => [s, g.tac])),
        };
      });
    onConfirm(servers);
  };

  const checkedCount = (groups ?? []).filter((g) => g.checked).length;

  return (
    <Box sx={{ border: '1px dashed', borderColor: 'primary.light', borderRadius: 2, p: 2, bgcolor: 'primary.50' }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
        <Typography sx={{ fontSize: '0.78rem', fontWeight: 700, color: 'primary.main' }}>
          🔍 Lookup by Site
        </Typography>
        <IconButton size="small" onClick={onCancel}><CloseIcon fontSize="small" /></IconButton>
      </Box>

      {/* Input */}
      <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
        <TextField
          size="small" fullWidth multiline maxRows={3}
          label="Sites (comma-separated)"
          placeholder="e.g. SITE1, SITE2, SITE3"
          value={siteInput}
          onChange={(e) => setSiteInput(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleLookup(); } }}
          disabled={loading}
        />
        <Button
          variant="contained" size="small"
          onClick={handleLookup}
          disabled={loading || !siteInput.trim()}
          sx={{ mt: 0.25, whiteSpace: 'nowrap', minWidth: 80 }}
        >
          {loading ? <CircularProgress size={14} color="inherit" /> : 'Lookup'}
        </Button>
      </Box>

      {/* Error */}
      {error && <Typography sx={{ fontSize: '0.72rem', color: 'error.main', mt: 1 }}>{error}</Typography>}

      {/* Not found */}
      {notFound.length > 0 && (
        <Box sx={{ mt: 1.5, p: 1, bgcolor: 'warning.50', border: '1px solid', borderColor: 'warning.light', borderRadius: 1 }}>
          <Typography sx={{ fontSize: '0.72rem', fontWeight: 600, color: 'warning.dark' }}>
            Not found in DB:
          </Typography>
          <Typography sx={{ fontSize: '0.71rem', color: 'warning.dark', fontFamily: 'monospace' }}>
            {notFound.join(', ')}
          </Typography>
        </Box>
      )}

      {/* No results */}
      {groups !== null && groups.length === 0 && !notFound.length && (
        <Typography sx={{ fontSize: '0.72rem', color: 'text.secondary', mt: 1.5 }}>
          No results found for the given sites and technology.
        </Typography>
      )}

      {/* Results */}
      {groups !== null && groups.length > 0 && (
        <Box sx={{ mt: 1.5 }}>
          <Typography sx={{ fontSize: '0.72rem', color: 'text.secondary', mb: 0.75 }}>
            Found <strong>{groups.length}</strong> Server/TAC group{groups.length !== 1 ? 's' : ''}.
            Deselect any you don't want to add:
          </Typography>

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, mb: 1.5 }}>
            {groups.map((g, idx) => (
              <Box
                key={`${g.omcId}||${g.tac}`}
                onClick={() => toggleGroup(idx)}
                sx={{
                  display: 'flex', alignItems: 'center', gap: 1, p: 0.75,
                  borderRadius: 1, cursor: 'pointer',
                  bgcolor: g.checked ? 'background.paper' : 'action.disabledBackground',
                  border: '1px solid', borderColor: g.checked ? 'primary.light' : 'divider',
                  opacity: g.checked ? 1 : 0.55, transition: 'all 0.15s',
                }}
              >
                <Checkbox checked={g.checked} size="small" sx={{ p: 0 }}
                  onClick={(e) => e.stopPropagation()} onChange={() => toggleGroup(idx)} />
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography sx={{ fontSize: '0.75rem', fontWeight: 600, fontFamily: 'monospace' }}>
                    {g.omcId} · TAC {g.tac}
                  </Typography>
                  <Typography sx={{ fontSize: '0.68rem', color: 'text.secondary', fontFamily: 'monospace' }} noWrap>
                    {g.sites.join(', ')}
                  </Typography>
                </Box>
                <Chip label={`${g.sites.length} site${g.sites.length !== 1 ? 's' : ''}`}
                  size="small" sx={{ height: 18, fontSize: '0.65rem' }} />
              </Box>
            ))}
          </Box>

          <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
            <Button size="small" color="inherit" onClick={onCancel}>Cancel</Button>
            <Button size="small" variant="contained" onClick={handleConfirm} disabled={checkedCount === 0}>
              Add {checkedCount} Server{checkedCount !== 1 ? 's' : ''}
            </Button>
          </Box>
        </Box>
      )}
    </Box>
  );
};


// =============================================================================
// TrackerModal ← exportado
//
// Props:
//   open           → boolean
//   entry          → objecto da API (null = criar, objecto = editar)
//   saving         → boolean
//   onSave         → callback(formData)
//   onClose        → callback()
//   onDelete       → callback(id)
//   onOpenSiteList → callback(server, updateFn)
//                    O TrackerPage recebe o server e uma função que deve chamar
//                    com os selectedSites quando o utilizador confirmar.
// =============================================================================
const TrackerModal = ({ open, entry, saving, loadingOne = false, onSave, onClose, onDelete, onOpenSiteList, userRole = null, userId = null, canDelete = true, privilegeVendor = 'Both' }) => {
  const isEditing  = Boolean(entry?.id);
  const isCopy     = !isEditing && Boolean(entry?.title);  // pre-filled but no id
  const entryReady = !isEditing || (!loadingOne && Boolean(entry?.createdBy));
  const isOwner    = isEditing && String(entry?.createdBy) === String(userId);
  const canSave    = userRole === null || userRole === 'Admin' || !isEditing || (entryReady && isOwner);

  const [form,          setForm]          = useState(emptyForm());
  const [errors,        setErrors]        = useState({});
  const [technologies,  setTechnologies]  = useState([]);
  const [vendors,       setVendors]       = useState([]);
  const [allVendors,    setAllVendors]    = useState([]);
  const [features,      setFeatures]      = useState([]);
  const [teams,         setTeams]         = useState([]);
  const [owners,        setOwners]        = useState([]);
  const [loadingOwners, setLoadingOwners] = useState(false);


  // ── Carregar lookups uma vez ao montar ──────────────────────────────────────
  useEffect(() => {
    Promise.all([apiGetTechnologies(), apiGetVendors(), apiGetFeatures(), apiGetTeams()])
      .then(([t, v, f, te]) => {
        setTechnologies(t.data.map((x) => x.name));
        const vAll = v.data.map((x) => x.name);
        setAllVendors(vAll);
        setVendors((!privilegeVendor || privilegeVendor === 'Both') ? vAll : vAll.filter((x) => x === privilegeVendor));
        setFeatures(f.data.map((x) => x.name));
        setTeams(te.data);
      })
      .catch(() => {});
  }, []);


  // ── Re-filtrar vendors quando privilegeVendor chega (async) ────────────────
  useEffect(() => {
    if (!allVendors.length) return;
    setVendors((!privilegeVendor || privilegeVendor === 'Both')
      ? allVendors
      : allVendors.filter((x) => x === privilegeVendor));
  }, [privilegeVendor, allVendors]);

  // ── Carregar owners quando o team muda ──────────────────────────────────────
  useEffect(() => {
    if (!form.teamId) { setOwners([]); return; }
    setLoadingOwners(true);
    apiGetOwnersByTeam(form.teamId)
      .then((res) => setOwners(res.data))
      .catch(() => setOwners([]))
      .finally(() => setLoadingOwners(false));
  }, [form.teamId]);


  // ── Popular o form quando o modal abre ──────────────────────────────────────
  useEffect(() => {
    if (!open) return;
    setErrors({});
    if (!entry) { setForm(emptyForm()); return; }

    setForm({
      ...emptyForm(),
      ...entry,

      // Fix 1: normalizar datas para "yyyy-MM-dd" (API pode devolver "dd/MM/yyyy HH:mm:ss")
      startDate: toDateInput(entry.startDate),
      endDate:   toDateInput(entry.endDate),

      // Fix 2: IDs como strings — MUI Select compara por === e a API devolve números
      teamId:    entry.teamId != null ? String(entry.teamId) : '',
      // Suporta tanto ownerIds[] (novo) como ownerId (legado — entries antigas na BD)
      ownerIds:  entry.ownerIds?.length
        ? entry.ownerIds.map(String)
        : (entry.ownerId != null ? [String(entry.ownerId)] : []),
      ownerNames: entry.ownerNames?.length
        ? entry.ownerNames
        : (entry.ownerName ? [entry.ownerName] : []),

      servers: (entry.servers?.length ? entry.servers : []).map(normalizeServer),
      // Normaliza a data de cada phase (pode vir em vários formatos da API)
      phases:  entry.phases?.length
        ? entry.phases.map((p) => ({ ...p, date: toDateInput(p.date) }))
        : [],
      status: entry.status ?? 'Open',
    });
  }, [open, entry]);


  // ── Handlers de campos simples ───────────────────────────────────────────────
  const handleChange = (e) =>
    setForm((prev) => ({ ...prev, [e.target.name]: e.target.value }));

  // Ao mudar a tecnologia, limpa os servers (dependem de technology+vendor)
  const handleTechnologyChange = (e) =>
    setForm((prev) => ({ ...prev, technology: e.target.value, servers: [] }));

  // Ao mudar o vendor, limpa igualmente os servers (dependem de technology+vendor)
  const handleVendorChange = (e) =>
    setForm((prev) => ({ ...prev, vendor: e.target.value, servers: [] }));

  // Team: actualiza nome e limpa owner (owner depende do team)
  const handleTeamChange = (e) => {
    const teamId   = e.target.value;
    const teamName = teams.find((t) => t.id === Number(teamId))?.name ?? '';
    setForm((prev) => ({ ...prev, teamId, teamName, ownerIds: [], ownerNames: [] }));
  };

  const handleOwnerChange = (e) => {
    // MUI Select multiple devolve sempre um array
    const ownerIds   = (Array.isArray(e.target.value) ? e.target.value : [e.target.value]).map(String);
    const ownerNames = ownerIds.map((id) => owners.find((o) => String(o.id) === id)?.name ?? '').filter(Boolean);
    setForm((prev) => ({ ...prev, ownerIds, ownerNames }));
  };


  // ── Handlers de Phases ───────────────────────────────────────────────────────
  const addPhase = () => {
    const n = form.phases.filter((p) => p.label !== 'Rollback').length + 1;
    setForm((prev) => ({ ...prev, phases: [...prev.phases, makePhase(`Phase ${n}`)] }));
  };

  const addRollback = () => {
    const n = form.phases.filter((p) => p.label?.startsWith('Rollback')).length + 1;
    const label = n === 1 ? 'Rollback' : `Rollback ${n}`;
    setForm((prev) => ({ ...prev, phases: [...prev.phases, makePhase(label)] }));
  };

  const updatePhase = (id, updated) =>
    setForm((prev) => ({ ...prev, phases: prev.phases.map((p) => p.id === id ? updated : p) }));

  const removePhase = (id) =>
    setForm((prev) => ({ ...prev, phases: prev.phases.filter((p) => p.id !== id) }));


  // ── Handlers de Servers ──────────────────────────────────────────────────────
  const [showSiteLookup, setShowSiteLookup] = useState(false);

  const addServer = () =>
    setForm((prev) => ({ ...prev, servers: [...prev.servers, makeServer()] }));

  const addServersBySite = (newServers) => {
    setForm((prev) => ({ ...prev, servers: [...prev.servers, ...newServers] }));
    setShowSiteLookup(false);
  };

  const updateServer = (id, updated) =>
    setForm((prev) => ({ ...prev, servers: prev.servers.map((s) => s.id === id ? updated : s) }));

  const removeServer = (id) =>
    setForm((prev) => ({ ...prev, servers: prev.servers.filter((s) => s.id !== id) }));


  // ── Handler do Site List ─────────────────────────────────────────────────────
  // Passa ao TrackerPage o server E uma função de update com setForm funcional.
  // setForm funcional é crítico: lê sempre o state mais recente (evita closure stale).
  const handleOpenSiteList = (server) => {
    onOpenSiteList(
      server,
      (selectedSites) => {
        // Distribute the flat selectedSites list back into each tac's sites array
        const siteToTac = server.siteToTac ?? {};
        const updatedTacs = (server.tacs ?? []).map((t, idx) => ({
          ...t,
          sites: selectedSites.filter(site =>
            siteToTac[site] === t.tac ||
            (!siteToTac[site] && idx === 0)
          ),
        }));
        setForm((prev) => ({
          ...prev,
          servers: prev.servers.map((s) =>
            s.id === server.id
              ? { ...s, selectedSites, tacs: updatedTacs }
              : s
          ),
        }));
      }
    );
  };


  // ── Save e Delete ────────────────────────────────────────────────────────────
  const validate = () => {
    const e = {};
    if (!form.title?.trim())      e.title      = 'Title is required';
    if (!form.technology)         e.technology  = 'Technology is required';
    if (!form.vendor)             e.vendor      = 'Vendor is required';
    if (!form.feature)            e.feature     = 'Type is required';
    if (!form.startDate)          e.startDate   = 'Start Date is required';
    if (form.startDate && form.endDate && form.endDate < form.startDate)
                                  e.endDate     = 'End Date cannot be before Start Date';
    if (!form.teamId)             e.teamId      = 'Team is required';
    if (!form.ownerIds?.length)   e.ownerIds    = 'Owner is required';
    return e;
  };

  const handleSave = () => {
    const e = validate();
    if (Object.keys(e).length > 0) { setErrors(e); return; }
    setErrors({});
    onSave(isEditing ? { ...form, id: entry.id } : form);
  };

  const handleDelete = () => {
    if (window.confirm('Remove this activity? This cannot be undone.')) {
      onDelete(entry.id);
    }
  };



  // ── Render ───────────────────────────────────────────────────────────────────
  return (
    <Dialog
      open={open}
      onClose={() => {}}
      disableEscapeKeyDown
      maxWidth="md"
      fullWidth
      scroll="paper"
    >
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pb: 1 }}>
        <Box sx={{ fontWeight: 700, fontSize: '1.1rem' }}>
          {isEditing ? 'Edit Activity' : isCopy ? 'Copy Activity' : 'New Activity'}
        </Box>
        <IconButton onClick={onClose} size="small" disabled={saving}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <Divider />

      <DialogContent sx={{ pt: 2.5 }}>

        {/* ── Banner: Tourist — view only ── */}
        {isEditing && userRole === 'Tourist' && (
          <Alert severity="info" sx={{ mb: 2 }}>
            You have view-only access — changes cannot be saved.
          </Alert>
        )}

        {/* ── Banner: Regular não é owner ── */}
        {isEditing && entryReady && userRole === 'Regular' && !isOwner && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            This activity was created by another user — you cannot edit it.
          </Alert>
        )}

        {/* ── Identity ── */}
        <SectionLabel>Identity</SectionLabel>
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid size={12}>
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
              <TextField
                fullWidth label="Title *" name="title"
                value={form.title}
                onChange={(e) => { handleChange(e); setErrors((p) => ({ ...p, title: undefined })); }}
                placeholder="e.g. 5G Carrier Aggregation Feature Rollout"
                error={Boolean(errors.title)}
                helperText={errors.title}
              />
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', pt: 1, minWidth: 70 }}>
                <Switch
                  size="small"
                  checked={form.status === 'Open'}
                  onChange={(e) => setForm((prev) => ({ ...prev, status: e.target.checked ? 'Open' : 'Closed' }))}
                  disabled={saving}
                />
                <Typography variant="caption"
                  sx={{ color: form.status === 'Open' ? 'success.main' : 'text.disabled', fontWeight: 500, mt: 0.25 }}>
                  {form.status === 'Open' ? 'Open' : 'Closed'}
                </Typography>
              </Box>
            </Box>
          </Grid>
        </Grid>

        {/* ── Classification ── */}
        <SectionLabel>Classification</SectionLabel>
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid size={{ xs: 12, sm: 4 }}>
            <TextField
              fullWidth select label="Technology *" name="technology"
              value={form.technology}
              onChange={(e) => { handleTechnologyChange(e); setErrors((p) => ({ ...p, technology: undefined })); }}
              error={Boolean(errors.technology)}
              helperText={errors.technology}
            >
              {technologies.map((o) => <MenuItem key={o} value={o}>{o}</MenuItem>)}
            </TextField>
          </Grid>
          <Grid size={{ xs: 12, sm: 4 }}>
            <TextField
              fullWidth select label="Vendor *" name="vendor"
              value={form.vendor}
              onChange={(e) => { handleVendorChange(e); setErrors((p) => ({ ...p, vendor: undefined })); }}
              error={Boolean(errors.vendor)}
              helperText={errors.vendor}
            >
              {vendors.map((o) => <MenuItem key={o} value={o}>{o}</MenuItem>)}
            </TextField>
          </Grid>
          <Grid size={{ xs: 12, sm: 4 }}>
            <TextField
              fullWidth select label="Type *" name="feature"
              value={form.feature}
              onChange={(e) => { handleChange(e); setErrors((p) => ({ ...p, feature: undefined })); }}
              error={Boolean(errors.feature)}
              helperText={errors.feature}
            >
              {features.map((o) => <MenuItem key={o} value={o}>{o}</MenuItem>)}
            </TextField>
          </Grid>
        </Grid>

        {/* ── Timeline ── */}
        <SectionLabel>Timeline</SectionLabel>
        <LocalizationProvider dateAdapter={AdapterMoment}>
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid size={{ xs: 12, sm: 6 }}>
              <DatePicker
                label="Start Date *"
                format="YYYY-MM-DD"
                value={form.startDate ? moment(form.startDate, 'YYYY-MM-DD') : null}
                onChange={(val) => {
                  const str = val?.isValid() ? val.format('YYYY-MM-DD') : '';
                  setForm((p) => ({ ...p, startDate: str }));
                  setErrors((p) => ({ ...p, startDate: undefined, endDate: undefined }));
                }}
                slotProps={{
                  textField: {
                    fullWidth: true,
                    size: 'small',
                    error: Boolean(errors.startDate),
                    helperText: errors.startDate,
                  },
                }}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <DatePicker
                label="End Date"
                format="YYYY-MM-DD"
                value={form.endDate ? moment(form.endDate, 'YYYY-MM-DD') : null}
                onChange={(val) => {
                  const str = val?.isValid() ? val.format('YYYY-MM-DD') : '';
                  setForm((p) => ({ ...p, endDate: str }));
                  setErrors((p) => ({ ...p, endDate: undefined }));
                }}
                minDate={form.startDate ? moment(form.startDate, 'YYYY-MM-DD') : undefined}
                slotProps={{
                  textField: {
                    fullWidth: true,
                    size: 'small',
                    error: Boolean(errors.endDate),
                    helperText: errors.endDate,
                  },
                }}
              />
            </Grid>
          </Grid>
        </LocalizationProvider>

        {/* ── Servers ── */}
        <SectionLabel>Servers</SectionLabel>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, mb: 3 }}>
          {form.servers.map((server) => (
            <ServerCard
              key={server.id}
              server={server}
              technology={form.technology}
              vendor={form.vendor}
              onChange={(updated) => updateServer(server.id, updated)}
              onRemove={() => removeServer(server.id)}
              onOpenSiteList={handleOpenSiteList}
            />
          ))}

          {/* Site Lookup card — shown when Add by Site is clicked */}
          {showSiteLookup && (
            <SiteLookupCard
              technology={form.technology}
              onConfirm={addServersBySite}
              onCancel={() => setShowSiteLookup(false)}
            />
          )}

          {/* Buttons */}
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              variant="outlined" size="small" startIcon={<AddIcon />}
              onClick={addServer}
            >
              Add Server
            </Button>
            <Button
              variant="outlined" size="small" startIcon={<AddIcon />}
              onClick={() => setShowSiteLookup(true)}
              disabled={showSiteLookup}
              color="primary"
            >
              Add by Site
            </Button>
          </Box>
        </Box>

        {/* ── Phases ── */}
        <SectionLabel>Phases</SectionLabel>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, mb: 3 }}>
          {form.phases.map((ph) => (
            <PhaseCard
              key={ph.id}
              phase={ph}
              onChange={(updated) => updatePhase(ph.id, updated)}
              onRemove={() => removePhase(ph.id)}
            />
          ))}
          <Box sx={{ display: 'flex', gap: 1, mt: 0.5 }}>
            <Button variant="outlined" size="small" startIcon={<AddIcon />} onClick={addPhase} sx={{ flex: 2 }}>
              Add Phase
            </Button>
            <Button
              variant="outlined" size="small" color="error"
              startIcon={<AddIcon />} onClick={addRollback}
              sx={{ flex: 1 }}
            >
              Add Rollback
            </Button>
          </Box>
        </Box>

        {/* ── Ownership ── */}
        <SectionLabel>Ownership</SectionLabel>
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid size={{ xs: 12, sm: 6 }}>
            <TextField
              fullWidth select label="Team *"
              value={teams.length === 0 ? '' : (form.teamId ?? '')}
              onChange={(e) => { handleTeamChange(e); setErrors((p) => ({ ...p, teamId: undefined })); }}
              error={Boolean(errors.teamId)}
              helperText={errors.teamId}
            >
              <MenuItem value=""><em>— Select team —</em></MenuItem>
              {teams.map((t) => <MenuItem key={t.id} value={String(t.id)}>{t.name}</MenuItem>)}
            </TextField>
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <TextField
              fullWidth select label="Activity Owner *"
              error={Boolean(errors.ownerIds)}
              helperText={errors.ownerIds}
              value={(owners.length === 0 || loadingOwners) ? [] : (form.ownerIds ?? [])}
              onChange={(e) => { handleOwnerChange(e); setErrors((p) => ({ ...p, ownerIds: undefined })); }}
              disabled={!form.teamId || loadingOwners}
              SelectProps={{
                multiple: true,
                renderValue: (selected) =>
                  selected.length === 0
                    ? <em>{form.teamId ? '— Select owner —' : 'Select a team first'}</em>
                    : selected
                        .map((id) => owners.find((o) => String(o.id) === id)?.name ?? id)
                        .join(', '),
              }}
              slotProps={{
                input: {
                  endAdornment: loadingOwners
                    ? <InputAdornment position="end"><CircularProgress size={14} /></InputAdornment>
                    : undefined,
                },
              }}
            >
              {owners.length === 0 && !loadingOwners && (
                <MenuItem value="" disabled>
                  <em>{form.teamId ? 'No owners available' : 'Select a team first'}</em>
                </MenuItem>
              )}
              {owners.map((o) => (
                <MenuItem key={o.id} value={String(o.id)}>
                  <Checkbox checked={(form.ownerIds ?? []).includes(String(o.id))} size="small" sx={{ mr: 1, p: 0 }} />
                  {o.name}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
        </Grid>

        {/* ── Comments ── */}
        <SectionLabel>Comments</SectionLabel>
        <TextField
          fullWidth multiline rows={3}
          label="General Comments" name="comments"
          value={form.comments ?? ''}
          onChange={handleChange}
          placeholder="Observations, KPIs to monitor, dependencies…"
        />

      </DialogContent>
      <Divider />

      <DialogActions sx={{ px: 3, py: 2, justifyContent: 'space-between' }}>
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
          {isEditing && (canDelete || (userRole === 'Regular' && entry?.createdBy === userId)) && (
            <Button
              variant="outlined" color="error"
              startIcon={<DeleteOutlineIcon />}
              onClick={handleDelete}
              disabled={saving}
            >
              Delete Activity
            </Button>
          )}
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button onClick={onClose} color="inherit" disabled={saving}>
            Cancel
          </Button>
          <Button onClick={handleSave} variant="contained" disabled={saving || !canSave}>
            {saving
              ? <CircularProgress size={18} color="inherit" />
              : isEditing ? 'Save Changes' : isCopy ? 'Create Copy' : 'Create Activity'
            }
          </Button>
        </Box>
      </DialogActions>
    </Dialog>
  );
};

export default TrackerModal;